import { Controller } from '@hotwired/stimulus'

/**
 * Stimulus-контроллер для многостраничной формы.
 *
 * Показывает активную страницу, переключает наборы кнопок действий,
 * валидирует текущий шаг перед переходом вперёд и переводит фокус на
 * релевантные поля.
 *
 * @extends {Controller}
 */
export default class MultipageFormController extends Controller {
  /**
   * Страницы формы и панели действий, привязанные к конкретным страницам.
   *
   * @type {string[]}
   */
  static targets = ['page', 'actions']

  /**
   * Индекс текущей страницы и флаг валидации при переходе вперёд.
   *
   * @type {{ currentPage: NumberConstructor, validate: { type: BooleanConstructor, default: boolean } }}
   */
  static values = {
    currentPage: Number,
    validate: { type: Boolean, default: true }
  }

  /**
   * Определяет стартовую страницу и отображает её после подключения
   * контроллера к DOM.
   *
   * @returns {void}
   */
  connect() {
    this.currentPageValue = this.resolveInitialPage()
    this.showPage(this.currentPageValue)
  }

  /**
   * Переходит на страницу, указанную в `data-next-page` кнопки действия.
   *
   * При переходе вперёд предварительно валидирует текущую страницу, если
   * включено значение `validate`.
   *
   * @param {Event} event - Событие активации кнопки перехода.
   * @returns {void}
   */
  nextPage(event) {
    event.preventDefault()

    const nextPage = Number(event.currentTarget.dataset.nextPage)
    if (Number.isNaN(nextPage)) return

    const isForward = nextPage > this.currentPageValue

    if (isForward && this.validateValue && !this.validateCurrentPage()) {
      this.focusFirstInvalidField()
      return
    }

    this.currentPageValue = nextPage
    this.showPage(nextPage)
    this.scrollToPageTop()
    this.focusFirstField()
  }

  /**
   * Валидирует текущую (последнюю) страницу перед отправкой формы.
   *
   * Нужна потому, что submit-кнопка живёт на последней странице и не проходит
   * через `nextPage`: без этой проверки обязательные поля последнего шага
   * (например чекбокс согласия) не валидировались бы на клиенте, и форма
   * уходила бы на сервер с ошибкой (ререндер модалки). При невалидной странице
   * отменяем отправку и подсвечиваем поле — модалка не перерисовывается.
   *
   * @param {Event} event - Событие отправки формы.
   * @returns {void}
   */
  validateOnSubmit(event) {
    if (!this.validateValue) return
    if (this.validateCurrentPage()) return

    event.preventDefault()
    this.focusFirstInvalidField()
  }

  /**
   * Вычисляет стартовую страницу формы.
   *
   * Приоритет: явно переданное значение, страница с ошибками, уже видимая
   * страница, первая страница формы.
   *
   * @returns {number} Индекс страницы, которую нужно показать первой.
   */
  resolveInitialPage() {
    if (this.hasCurrentPageValue) return this.currentPageValue

    const pageWithError = this.pageTargets.find((page) =>
      page.querySelector('.is-invalid, .hint-text--error')
    )
    if (pageWithError) return this.pageIndex(pageWithError)

    const visiblePage = this.pageTargets.find(
      (page) => !page.classList.contains('hidden')
    )
    if (visiblePage) return this.pageIndex(visiblePage)

    return this.pageTargets.length ? this.pageIndex(this.pageTargets[0]) : 0
  }

  /**
   * Показывает страницу формы и соответствующую ей панель действий.
   *
   * @param {number} pageIndex - Индекс страницы из `data-page-index`.
   * @returns {void}
   */
  showPage(pageIndex) {
    this.pageTargets.forEach((page) => {
      const isActive = this.pageIndex(page) === pageIndex

      page.classList.toggle('hidden', !isActive)
      page.toggleAttribute('inert', !isActive)
      page.setAttribute('aria-hidden', String(!isActive))
    })

    this.actionsTargets.forEach((actions) => {
      actions.classList.toggle(
        'hidden',
        Number(actions.dataset.page) !== pageIndex
      )
    })

    this.dispatch('changed', { detail: { page: pageIndex } })
  }

  /**
   * Проверяет валидность текущей страницы формы.
   *
   * Учитывает вложенные Stimulus-контроллеры с методом `validate()` и
   * нативную HTML-валидацию полей.
   *
   * @returns {boolean} `true`, если текущая страница может быть отправлена или пропущена.
   */
  validateCurrentPage() {
    const currentPage = this.currentPage
    if (!currentPage) return true

    const controllersValid = this.validatableControllers(currentPage).every(
      (controller) => {
        const result = controller.validate()

        return (
          result !== false &&
          !controller.element.classList.contains('is-invalid')
        )
      }
    )

    const fieldsValid = this.validatableFields(currentPage).every((field) =>
      field.checkValidity()
    )

    return controllersValid && fieldsValid
  }

  /**
   * Находит вложенные контроллеры, которые умеют валидировать своё состояние.
   *
   * @param {HTMLElement} page - Страница формы, внутри которой ищутся контроллеры.
   * @returns {Controller[]} Список контроллеров с методом `validate()`.
   */
  validatableControllers(page) {
    return Array.from(page.querySelectorAll('[data-controller]')).flatMap(
      (element) =>
        element.dataset.controller
          .split(/\s+/)
          .map((identifier) =>
            this.application.getControllerForElementAndIdentifier(
              element,
              identifier
            )
          )
          .filter((controller) => controller?.validate)
    )
  }

  /**
   * Возвращает поля страницы, пригодные для нативной HTML-валидации.
   *
   * @param {HTMLElement} page - Страница формы, внутри которой ищутся поля.
   * @returns {(HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[]} Список активных видимых полей.
   */
  validatableFields(page) {
    return Array.from(page.querySelectorAll('input, select, textarea')).filter(
      (field) => !field.disabled && field.type !== 'hidden'
    )
  }

  /**
   * Прокручивает форму к первому невалидному элементу текущей страницы и
   * переводит фокус на связанное поле.
   *
   * @returns {void}
   */
  focusFirstInvalidField() {
    const invalidElement = this.currentPage?.querySelector(
      '.is-invalid, input:invalid, select:invalid, textarea:invalid'
    )

    if (!invalidElement) return

    invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

    const field = invalidElement.matches('input, select, textarea')
      ? invalidElement
      : invalidElement.querySelector('input, select, textarea, button')

    field?.focus({ preventScroll: true })
  }

  /**
   * Переводит фокус на первое доступное поле или кнопку текущей страницы.
   *
   * @returns {void}
   */
  focusFirstField() {
    const field = this.currentPage?.querySelector(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled])'
    )

    field?.focus({ preventScroll: true })
  }

  /**
   * Прокручивает верх модального окна или страницы к началу формы.
   *
   * @returns {void}
   */
  scrollToPageTop() {
    const scrollContainer =
      this.element.closest('[data-scroll-lock-scrollable]') ||
      this.element.closest('.modal__content')

    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /**
   * Возвращает числовой индекс страницы из `data-page-index`.
   *
   * @param {HTMLElement} page - DOM-элемент страницы формы.
   * @returns {number} Индекс страницы.
   */
  pageIndex(page) {
    return Number(page.dataset.pageIndex)
  }

  /**
   * Возвращает текущую активную страницу формы.
   *
   * @returns {HTMLElement | undefined} Страница с индексом `currentPageValue`.
   */
  get currentPage() {
    return this.pageTargets.find(
      (page) => this.pageIndex(page) === this.currentPageValue
    )
  }
}
